<template lang="pug" src="./history.pug"></template>
<style lang="scss" src="./history.scss" scoped></style>

<script>
import Vue from 'vue';
import {mapState, mapGetters} from 'vuex';

import InfoOrder from '../ReceiveOrders/info-order';
import request from '@/utils/request';
import config from '../../../../config.json';
import {VAutocomplete} from "vuetify/lib/components";
import {localizeField} from "@/helpers/locales";

const RESET_FILTERS_TIMEOUT = 1.5 * 1000;

export default {
  filters: {
      cookTime(item) {
        if (item.deliveryCookTime) {
          const hoursSinceCreated = new Date(item.deliveryCookTime).hoursSince(new Date(item.createdAt)),
              minutesSinceCreated = +new Date(item.deliveryCookTime).minutesSince(new Date(item.createdAt)) - hoursSinceCreated * 60;

          return `${String(hoursSinceCreated).padLeft(2, '0')}:${String(minutesSinceCreated).padLeft(2, '0')}`;
        } else {
          return '';
        }
      }
  },
  components: {
    'v-info-order': InfoOrder,
    'v-autocomplete': VAutocomplete
  },
  props: {
    modalFilters: {
      type: Boolean,
      default: false
    },
    redirectAfterEdit: {
      type: Boolean,
      default: true
    }
  },
  data: function () {
    return {
      items: [],
      showCancelDeliveryModal: false,
      deliveryCancelReason: '',
      cancellingDeliveryOrderId: null,
      pagination: {
        page: 1,
        totalPages: 1,
        count: 0,
        limit: 0
      },
      showFilters: false,
      defaultChosenReasons: {
        key: 'all',
        value: 'all'
      },
      reasons: config.reasons.filter(it => it.use || it.forHistory),
      statuses: ['all', 'new', 'waiting', 'searching', 'accepted', 'arrived', 'landed', 'completed', 'cancelled'],
      deliveryStatuses: ['all', 'new', 'accepted', 'canceledKitchen', 'notAccepted', 'paid', 'paymentWait', 'cooking', 'canceledClient', 'canceledClientPayment', 'readyForDelivery', 'handedToCourier', 'delivered'],
      scopes: [
        {
          name: 'all',
          value: null
        },
        {
          name: 'taxi',
          value: false
        },
        {
          name: 'delivery',
          value: true
        }
      ],
      filters: {
        startDate: new Date().addDays(-1).format('{yyyy}-{MM}-{dd}'),
        endDate: new Date().addDays(1).format('{yyyy}-{MM}-{dd}'),
        managerPhoneNumber: null,
        managerInfo: null,
        startTime: '00:00',
        endTime: '23:59',
        orderId: null,
        clientInfo: null,
        isDelivery: this.modalFilters ? null : false,
        orderAddress: null,
        orderComments: null,
        orderOptions: null,
        driverInfo: null,
        operatorInfo: null,
        chosenReason: null,
        chosenStatus: 'all',
        questionType: null,
        chosenDeliveryStatus: 'all',
        brandId: null,
        establishmentId: null
      },
      startDateModal: false,
      endDateModal: false,
      startTimeModal: false,
      endTimeModal: false,
      cancelRequest: false,
      editRequest: false,
      returnToSearchRequest: false,
      canResetAgain: true,
      canResetTableFilters: true
    };
  },
  computed: {
    ...mapGetters(['userBrandId', 'roleName', 'userEstablishmentId']),
    ...mapState({
      brands: state => state.data.brands,
      establishments: state => state.data.establishments,
      orderHistories: state => state.orders.orderHistories
    }),
    showRouteHeader() {
      return ['boss', 'admin', 'manager', 'accountant'].includes(this.roleName);
    },
    showOverlay() {
      return this.showFilters && this.modalFilters;
    },
    hideDeliveryCols() {
      return this.filters.isDelivery === false;
    },
    hideClientColumn() {
      return ['boss'].includes(this.roleName) && this.userBrandId;
    },
    isTinyView() {
      return ['manager-establishment', 'admin-establishment'].includes(this.roleName);
    },
    headers() {
      const properties = this.generateTableHeaders();

      if (this.hideClientColumn) {
        const clientColumnIndex = properties.indexOf('client');

        if (clientColumnIndex != -1) {
          properties.splice(clientColumnIndex, 1);
        }
      }

      if (!this.userEstablishmentId && this.filters.isDelivery !== false) {
        properties.splice(properties.length - 1, 0, 'establishment');
      }

      if (!this.userBrandId) {
        properties.splice(properties.length - 1, 0, 'brandId');
      }

      return properties.filter(it => !!it).map(it => ({
        text: this.$vuetify.lang.t(`$vuetify.pages.History.table.${it}`),
        value: it,
        sortable: false,
        align: 'center'
      }));
    },
    questionsTypes() {
      const types = config.orderCancelDetails.questionsTypes;
      const translationPath = '$vuetify.pages.ReceiveOrders.cancelOrderDetails.questionsList';

      return types.map((key) => ({
        key,
        value: this.$vuetify.lang.t(`${translationPath}.${key}`)
      }));
    },
    canShowActions() {
      return ['operator', 'admin', 'corporate-admin', 'corporate-user', 'admin-delivery', 'manager-establishment'].includes(this.roleName);
    },
    canShowCancelReason() {
      return this.filters.chosenStatus === 'cancelled';
    },
    chosenReasons() {
      return [this.defaultChosenReasons, ...config.reasons.filter(it => it.user !== false)];
    },
    brandsSelect() {
      return [{
        id: null,
        title: this.$vuetify.lang.t(`$vuetify.pages.History.all`)
      }].concat(this.brands);
    },
    establishmentSelect() {
      return [{
        id: null,
        [`name:${this.$vuetify.lang.current}`]: this.$vuetify.lang.t(`$vuetify.pages.History.all`)
      }].concat(this.establishments);
    }
  },
  setup() {
      return {
         localizeField
      };
  },
  created() {
    this.$store.dispatch('data/getBrands');
    this.$store.dispatch('data/getEstablishments');
    this.loadItems();
  },
  methods: {
    async loadItems(page = 1, isScrollEnabled = false) {
      await request({
        method: 'get',
        url: '/orders/history',
        params: Object.assign({}, this.filters, {
          page
        })
      }).then(res => {
        res.data.items = res.data.items.map(order => {
          if (order.stayTime) {
            const date = new Date(null);

            date.setSeconds(order.stayTime);
            order.stayTime = date.toISOString().substr(14, 5);
          }

          order.popover = false;

          if (order.unorderedAddresses) {
            order.addresses = order.unorderedAddresses
                .sortBy('sortOrder')
                .map('place')
                .map(it => Vue.filter('extendAddress')(it));
          }

          if (order.establishment) {
            order.establishment.addresses[0].place = Vue.filter('extendAddress')(order.establishment.addresses[0].place);
          }

          return order;
        });

        this.pagination = {
          page: res.data.page || 1,
          totalPages: res.data.totalPages || 1,
          count: res.data.count || 0,
          limit: res.data.limit || 0
        };

        this.items = res.data.items;

        if (isScrollEnabled) {
          this.$refs.table.scrollIntoView();
        }
      }).catch(error => {
        if (error.response && error.response.data && error.response.data.error) {
          this.$store.dispatch('alert/addMessage', {
            message: error.response.data.error
          });
        }
      });
    },

    generateTableHeaders() {
      const route = this.showRouteHeader ? 'route' : null;

      let headers;

      if (this.filters.isDelivery === false) {
        headers = [
          'actions', 'id', 'createdAt', 'gettingOrderTime', 'status', 'statusesHistory',
          'client', 'address', 'comments', 'additionAddresses', 'car',
          'arrivalTime', 'routeLength', 'stayTime', 'options', 'operator', 'tips',
          'calculatedPrice', 'price', route];
      } else {
        headers = this.isTinyView ? [
          'actions', 'id', 'createdAt', 'status', 'deliveryCancelReason', 'courierStatus', 'statusesHistory', 'managerPhoneNumber', 'comments', 'products', 'cookTime', 'countOfPersons', 'problem', 'car',
          'manager'] : [
          'actions', 'id', 'createdAt', 'gettingOrderTime', 'status', 'deliveryCancelReason', 'courierStatus', 'statusesHistory',
          'client', 'address', 'managerPhoneNumber', 'comments', 'additionAddresses', 'products', 'cookTime', 'countOfPersons', 'problem', 'car',
          'arrivalTime', 'routeLength', 'stayTime', 'options', 'operator', 'manager', 'tips',
          'calculatedPrice', 'price', route
        ];
      }

      if (this.roleName === 'manager-establishment') {
        headers = headers.filter(i => i !== 'managerPhoneNumber');
      }

      return headers;
    },

    showReason(reason, status) {
      if (status === 'searching') {
        return true;
      }

      return !['not-order', 'no-more-cars'].includes(reason);
    },

    saveByRef(ref) {
      this.$refs[ref].save(this.filters[ref]);
      this.loadItems();
    },

    resetData(pickerType) {
      const action = {
        startDate: this.resetStartDate,
        endDate: this.resetEndDate
      };

      action[pickerType]();

      this.loadItems();
    },

    resetStartDate() {
      this.startDateModal = false;
      this.filters.startDate = new Date().addDays(-1).format('{yyyy}-{MM}-{dd}');
    },

    resetEndDate() {
      this.endDateModal = false;
      this.filters.endDate = new Date().addDays(1).format('{yyyy}-{MM}-{dd}');
    },

    applyFilters() {
      this.showFilters = false;
    },

    canWorkWithOrder(order) {
      if (!this.canShowActions) {
        return false;
      }

      const updatedAtMillis = Date.create(order.updatedAt).getTime();
      const oneDayAgoMillis = Date.create().addDays(-1).getTime();

      return ((updatedAtMillis > oneDayAgoMillis) || !['completed'].includes(order.status))
          && (this.canCancel(order) || this.canEdit(order) || this.canReturnToSearch(order));
    },

    canReturnToSearch(order) {
      const orderDate = new Date(order.updatedAt);
      const firstAddress = order.addresses && order.addresses[0];

      return new Date().addHours(-24) < orderDate
          && (!order.preOrderTime || new Date(order.preOrderTime) > new Date())
          && order.status === 'cancelled'
          && firstAddress && (firstAddress.street || firstAddress.name) && order.fromInternet
          && !['no-more-cars'].includes(order.cancelReason);
    },

    openDeliveryCancellation(order) {
      this.cancellingDeliveryOrder = order;
      this.showCancelDeliveryModal = true;
    },

    closeDeliveryCancellation() {
      this.cancellingDeliveryOrderId = null;
      this.showCancelDeliveryModal = false;
      this.deliveryCancelReason = '';
    },

    cancelDeliveryOrder() {
      if (this.deliveryCancelReason.length === 0 || this.cancellingDeliveryOrder === null) {
        return;
      }

      const callbackAction = () => this.closeDeliveryCancellation();
      const payload = {
        deliveryStatus: this.cancellingDeliveryOrder.deliveryStatus === 'new' ? 'notAccepted' : 'canceledKitchen',
        deliveryCancelReason: this.deliveryCancelReason,
      };
      this.changeDeliveryOrderStatus(payload, callbackAction);
    },

    changeDeliveryOrderStatus(data, closeDialogFn) {
      return request({
        method: 'POST',
        url: `/orders-delivery/${this.cancellingDeliveryOrder.id}/changeStatus`,
        data,
      }).then((res) => {
        if (res.status === 200) {
          closeDialogFn();
        }
      }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Error: ', err.response);
      });
    },

    canCancel(order) {
      if (['completed', 'cancelled'].includes(order.status)) {
          return false;
      }

      if (order.deliveryStatus && ['canceledKitchen', 'notAccepted'].includes(order.deliveryStatus)) {
        return false;
      }

      return true;
    },

    canEdit(order) {
      if (['arrived', 'landed', 'completed', 'cancelled'].includes(order.status)) {
        return false;
      }

      if (order.deliveryStatus && ['canceledKitchen', 'notAccepted'].includes(order.deliveryStatus)) {
        return false;
      }

      if (order.isLate && order.isLate !== false) {
        return false;
      }

      return true;
    },

    editOrder(orderId, isDelivery) {
      if (this.editRequest) {
        return;
      }

      this.editRequest = true;

      this.$store.dispatch('orders/editOrder', {orderId, isDelivery}).then(() => this.loadItems())
          .then(() => {
            if (this.redirectAfterEdit) {
              this.$router.push('/receive-orders');
            }

            this.$nextTick(() => {
              this.editRequest = false;
            });
          });
    },

    resetFilters() {
      if (this.canResetAgain) {
        this.filters.startDate = new Date().addDays(-1).format('{yyyy}-{MM}-{dd}');
        this.filters.endDate = new Date().addDays(1).format('{yyyy}-{MM}-{dd}');

        this.filters.startTime = '00:00';
        this.filters.endTime = '23:59';

        this.filters.chosenReason = null;
        this.filters.chosenStatus = 'all';
        this.filters.chosenDeliveryStatus = 'all';
        this.filters.brandId = null;
        this.filters.establishmentId = null;
        this.filters.isDelivery = this.modalFilters ? null : false;

        this.filters.questionType = null;

        this.loadItems();

        this.showFilters = false;
        this.canResetAgain = false;

        setTimeout(() => {
          this.canResetAgain = true;
        }, RESET_FILTERS_TIMEOUT);
      }
    },

    resetTableFilters() {
      if (this.canResetTableFilters) {
        Object.assign(this.filters, {
          orderId: null,
          orderAddress: null,
          clientInfo: null,
          orderComments: null,
          orderOptions: null,
          driverInfo: null,
          operatorInfo: null,
          managerInfo: null,
          managerPhoneNumber: null
        });

        this.loadItems();
        this.canResetTableFilters = false;

        setTimeout(() => {
          this.canResetTableFilters = true;
        }, RESET_FILTERS_TIMEOUT);
      }

    },

    changeStatus() {
      if (this.filters.chosenStatus !== 'cancelled') {
        this.filters.chosenReason = null;
      }


      if (this.filters.chosenStatus == 'cancelled' && !this.filters.chosenReason) {
        this.filters.chosenReason = this.defaultChosenReasons.key;
      }

      this.loadItems();
    },

    changeDeliveryStatus() {
      this.loadItems();
    },

    changeReason() {
      this.filters.questionType = null;
      this.loadItems();
    },

    changeScope(val) {
      this.filters.isDelivery = val;
      this.loadItems();
    },

    changeBrand() {
      this.loadItems();
    },

    changeEstablishment() {
      this.loadItems();
    },

    changeQuestionType() {
      this.loadItems();
    },

    cancelOrder(id, cancelReason) {
      if (this.cancelRequest) {
        return;
      }

      this.cancelRequest = true;

      this.$store.dispatch('orders/cancelOrder', {id, cancelReason, fromHistory: true})
          .then(() => this.loadItems())
          .finally(() => {
            this.$nextTick(() => {
              this.cancelRequest = false;
            });
          });
    },

    returnToSearch(item) {
      if (this.returnToSearchRequest) {
        return;
      }

      this.returnToSearchRequest = true;

      this.$store.dispatch('orders/returnToSearch', item.id)
          .then(() => this.loadItems())
          .then(() => {
            this.$nextTick(() => {
              this.returnToSearchRequest = false;
            });
          });
    }
  }
};
</script>
